/**
 * LeaveAReviewPage ✦ iter228 — /leave-a-review
 *
 * Public customer-facing review submission. Heritage styling, single
 * column, mobile-first. Pre-fills from `?b=<booking_id>&t=<token>`
 * query params when the link came from /admin/bookings.
 *
 * Submission lands in admin pending queue.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ServicesNav from '../components/services/ServicesNav';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';

const HERITAGE = {
  bg: '#F8F5EF', primary: '#4B371E', secondary: '#7A5C3E',
  accent: '#9C7C2E', accentSoft: '#C9B07A',
  text: '#2E2E2E', textDim: '#6B5E4F',
  cardBg: '#FFFFFF', cardShadow: '0 18px 60px rgba(75,55,30,0.12)',
};

const PRODUCTS = [
  { slug: '', label: '— Select product hired —' },
  { slug: 'blossom-booth', label: 'Blossom Booth' },
  { slug: 'woodland-mirror-booth', label: 'Mirror Booth' },
  { slug: 'heritage-booth', label: 'The Heritage Booth' },
  { slug: 'woodland-audio-guestbook', label: 'Audio Guestbook' },
  { slug: 'woodland-video-guestbook', label: 'Video Guestbook' },
  { slug: 'woodland-memory-post-box', label: 'Memory Post Box' },
  { slug: 'wedding-styling', label: 'Wedding Styling' },
  { slug: 'other', label: 'Other' },
];

const EVENT_TYPES = [
  { slug: '', label: '— Select event type —' },
  { slug: 'wedding', label: 'Wedding' },
  { slug: 'corporate', label: 'Corporate' },
  { slug: 'birthday', label: 'Birthday' },
  { slug: 'baby-shower', label: 'Baby Shower' },
  { slug: 'prom', label: 'Prom' },
  { slug: 'christmas', label: 'Christmas Party' },
  { slug: 'school', label: 'School Event' },
  { slug: 'charity', label: 'Charity' },
  { slug: 'festival', label: 'Festival' },
  { slug: 'other', label: 'Other' },
];

const labelStyle = {
  display: 'block', fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 12.5, letterSpacing: '0.05em',
  color: HERITAGE.textDim, marginBottom: 6,
};
const inputStyle = {
  width: '100%', fontFamily: SERIF, fontSize: 15,
  padding: '12px 14px', borderRadius: 8,
  border: `1px solid rgba(122,92,62,0.22)`,
  background: '#FFFFFF', color: HERITAGE.text,
  boxSizing: 'border-box',
};

const LeaveAReviewPage = () => {
  const [params] = useSearchParams();
  const booking_id = params.get('b') || '';
  const submission_token = params.get('t') || '';
  const [form, setForm] = useState({
    customer_name: '', customer_email: '',
    rating: 0, review_text: '',
    product: '', event_type: '', venue: '', event_date: '',
    display_consent: false, marketing_consent: false,
  });
  const [photos, setPhotos] = useState([]); // [{ url }]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    document.title = 'Leave a Review ✦ EventDrop';
  }, []);

  // Pre-fill from booking link
  useEffect(() => {
    if (!booking_id || !submission_token || prefilled) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/book/${submission_token}`);
        const b = r.data?.booking;
        if (!b) return;
        setForm((f) => ({
          ...f,
          customer_name: b.customer_name || f.customer_name,
          venue: b.venue || f.venue,
          event_date: b.event_date || f.event_date,
          product: b.package_slug || f.product,
        }));
        setPrefilled(true);
      } catch (e) { /* silent — link still works for blank form */ }
    })();
  }, [booking_id, submission_token, prefilled]);

  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const uploadOne = async (file) => {
    if (!file) return;
    if (photos.length >= 5) { toast.error('Max 5 photos.'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('Max 25MB per photo.'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/reviews/photo-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotos((p) => [...p, { url: r.data.image_url }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error('Please enter your name.'); return; }
    if (!form.rating) { toast.error('Please pick a star rating.'); return; }
    if (!form.review_text.trim()) { toast.error('Please write a short review.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/submit`, {
        ...form,
        image_urls: photos.map((p) => p.url),
        booking_id: booking_id || null,
        submission_token: submission_token || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  if (done) return <ThankYou />;

  return (
    <div data-testid="leave-review-page" style={{
      minHeight: '100vh', background: HERITAGE.bg, color: HERITAGE.text, fontFamily: SERIF,
    }}>
      <ServicesNav active="reviews" />
      <section style={{
        padding: 'clamp(60px, 9vw, 110px) clamp(18px, 5vw, 60px) 30px',
      }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase',
            color: HERITAGE.secondary, marginBottom: 16,
          }}>✦ Tell us about your experience</div>
          <h1 data-testid="leave-review-heading" style={{
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(28px, 4.5vw, 44px)', letterSpacing: '-0.02em',
            color: HERITAGE.primary, margin: '0 0 18px', lineHeight: 1.15,
          }}>We'd Love to Hear About Your Experience</h1>
          <p style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(14px, 1.5vw, 17px)',
            color: HERITAGE.textDim, lineHeight: 1.65, margin: 0,
          }}>
            Thank you for choosing EventDrop. Your feedback helps future couples and clients book with
            confidence, and it means the world to our small business.
          </p>
        </div>
      </section>

      <form onSubmit={submit} data-testid="leave-review-form" style={{
        maxWidth: 640, margin: '0 auto',
        padding: '0 clamp(18px, 5vw, 60px) 80px',
      }}>
        <div style={{
          background: HERITAGE.cardBg, padding: 'clamp(24px, 4vw, 40px)',
          borderRadius: 16, boxShadow: HERITAGE.cardShadow,
          border: `1px solid rgba(122,92,62,0.14)`,
        }}>
          <Field label="Star rating ✦ required">
            <StarPicker value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v }))} />
          </Field>
          <Field label="Your name ✦ required">
            <input data-testid="leave-review-name" type="text"
              value={form.customer_name} onChange={upd('customer_name')}
              style={inputStyle} required />
          </Field>
          <Field label="Email address (optional — never shown publicly)">
            <input data-testid="leave-review-email" type="email"
              value={form.customer_email} onChange={upd('customer_email')}
              style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Product hired">
              <select data-testid="leave-review-product"
                value={form.product} onChange={upd('product')} style={inputStyle}>
                {PRODUCTS.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Event type">
              <select data-testid="leave-review-event-type"
                value={form.event_type} onChange={upd('event_type')} style={inputStyle}>
                {EVENT_TYPES.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Venue">
              <input data-testid="leave-review-venue" type="text"
                value={form.venue} onChange={upd('venue')} style={inputStyle} />
            </Field>
            <Field label="Event date">
              <input data-testid="leave-review-event-date" type="date"
                value={form.event_date} onChange={upd('event_date')} style={inputStyle} />
            </Field>
          </div>
          <Field label="Your review ✦ required">
            <textarea data-testid="leave-review-text"
              value={form.review_text} onChange={upd('review_text')}
              rows={6} maxLength={3000}
              placeholder="Share a memory or two from your event ✦"
              style={{ ...inputStyle, fontFamily: SERIF, resize: 'vertical', minHeight: 130 }}
              required />
          </Field>
          <Field label={`Upload photos (max 5)`}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {photos.map((p, i) => (
                <div key={i} style={{
                  position: 'relative', width: 80, height: 80, borderRadius: 8,
                  backgroundImage: `url(${process.env.REACT_APP_BACKEND_URL}${p.url})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  border: `1px solid rgba(122,92,62,0.22)`,
                }} data-testid={`leave-review-photo-${i}`}>
                  <button type="button"
                    onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))}
                    style={{
                      position: 'absolute', top: -8, right: -8,
                      background: HERITAGE.primary, color: '#FFF8E7',
                      border: 'none', borderRadius: '50%', width: 22, height: 22,
                      cursor: 'pointer', fontFamily: SERIF, fontSize: 14, lineHeight: '20px',
                    }}>×</button>
                </div>
              ))}
              {photos.length < 5 && (
                <label data-testid="leave-review-upload" style={{
                  cursor: 'pointer', width: 80, height: 80, borderRadius: 8,
                  border: `1px dashed ${HERITAGE.accent}`, background: 'rgba(156,124,46,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: HERITAGE.accent, fontSize: 28, fontFamily: SERIF,
                }}>
                  {uploading ? '…' : '+'}
                  <input type="file" accept="image/*" hidden
                    onChange={(e) => uploadOne(e.target.files?.[0])} />
                </label>
              )}
            </div>
          </Field>
          <Field label="">
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              fontFamily: SERIF, fontSize: 13.5, color: HERITAGE.text,
              lineHeight: 1.55, cursor: 'pointer',
            }}>
              <input type="checkbox" checked={form.display_consent} onChange={upd('display_consent')}
                data-testid="leave-review-consent"
                style={{ marginTop: 3, accentColor: HERITAGE.accent, width: 16, height: 16 }} />
              I give EventDrop permission to display my review and uploaded photos on its
              website and social media.
            </label>
          </Field>
          <Field label="">
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              fontFamily: SERIF, fontSize: 13.5, color: HERITAGE.text,
              lineHeight: 1.55, cursor: 'pointer',
            }}>
              <input type="checkbox" checked={form.marketing_consent} onChange={upd('marketing_consent')}
                data-testid="leave-review-marketing"
                style={{ marginTop: 3, accentColor: HERITAGE.accent, width: 16, height: 16 }} />
              I'd like to hear about future EventDrop offers (optional).
            </label>
          </Field>
          <button type="submit" disabled={submitting}
            data-testid="leave-review-submit"
            style={{
              background: HERITAGE.accent, color: '#FFF8E7',
              border: 'none', borderRadius: 999,
              padding: '14px 28px', fontFamily: SERIF,
              fontSize: 13.5, letterSpacing: '0.14em', textTransform: 'uppercase',
              cursor: submitting ? 'wait' : 'pointer',
              boxShadow: '0 12px 30px rgba(156,124,46,0.32)',
              opacity: submitting ? 0.6 : 1, marginTop: 12, width: '100%',
            }}>
            {submitting ? 'Sending…' : 'Submit Review ✦'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 18 }}>
    {label && <label style={labelStyle}>{label}</label>}
    {children}
  </div>
);

const StarPicker = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div data-testid="leave-review-stars" style={{ display: 'flex', gap: 6 }}>
      {[1, 2, 3, 4, 5].map(n => {
        const active = (hover || value) >= n;
        return (
          <button key={n} type="button"
            data-testid={`leave-review-star-${n}`}
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            style={{
              background: 'transparent', border: 'none', padding: 4, cursor: 'pointer',
              fontSize: 34, color: active ? HERITAGE.accent : HERITAGE.accentSoft,
              transition: 'color 120ms ease, transform 120ms ease',
              transform: active ? 'scale(1.05)' : 'scale(1)',
            }}>★</button>
        );
      })}
    </div>
  );
};

const ThankYou = () => (
  <div data-testid="leave-review-thanks" style={{
    minHeight: '100vh', background: HERITAGE.bg, color: HERITAGE.text, fontFamily: SERIF,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '60px 24px',
  }}>
    <div style={{ maxWidth: 540, textAlign: 'center' }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: HERITAGE.secondary, marginBottom: 16,
      }}>✦ Received with thanks</div>
      <h1 style={{
        fontFamily: SERIF, fontWeight: 500,
        fontSize: 'clamp(32px, 5vw, 52px)', color: HERITAGE.primary,
        margin: '0 0 18px', letterSpacing: '-0.02em', lineHeight: 1.1,
      }}>Thank You!</h1>
      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 16, color: HERITAGE.textDim, lineHeight: 1.7, margin: '0 0 32px',
      }}>
        Your review has been received and is awaiting approval. We genuinely appreciate
        you taking the time to share your experience.
      </p>
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link to="/" data-testid="leave-review-home-link" style={{
          textDecoration: 'none', background: HERITAGE.accent, color: '#FFF8E7',
          borderRadius: 999, padding: '12px 24px', fontFamily: SERIF,
          fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>Return to Homepage</Link>
        <Link to="/photobooth-hire" data-testid="leave-review-photobooth-link" style={{
          textDecoration: 'none', background: 'transparent', color: HERITAGE.primary,
          border: `1px solid ${HERITAGE.secondary}`,
          borderRadius: 999, padding: '12px 24px', fontFamily: SERIF,
          fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>View Our Photobooths</Link>
      </div>
    </div>
  </div>
);

export default LeaveAReviewPage;
